import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { getAuthToken, queryClient } from "@/lib/queryClient";
import { useAuth } from "@/lib/auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Headphones, PhoneCall, Users, Clock } from "lucide-react";

type Agent = { id: string; name?: string; status: string; activeCalls?: number };
type Queue = { id: string; name: string; waiting?: number; agentsAvailable?: number };
type Call = { id: string; status: string; callerName?: string; callerPhone?: string; queueName?: string; agentName?: string; startedAt?: string };
type Summary = { totalCalls: number; answered: number; missed: number; avgWaitSec: number; avgTalkSec: number };

export default function SupervisorCallsDashboard() {
  const { user } = useAuth();
  const summary = useQuery<Summary>({ queryKey: ["/api/call-reports/summary"], refetchInterval: 15000 });
  const agents = useQuery<Agent[]>({ queryKey: ["/api/live-agents"], refetchInterval: 10000 });
  const queues = useQuery<Queue[]>({ queryKey: ["/api/call-queues"], refetchInterval: 10000 });
  const calls = useQuery<Call[]>({ queryKey: ["/api/calls"], refetchInterval: 5000 });

  useEffect(() => {
    if (!user) return;
    const proto = window.location.protocol === "https:" ? "wss" : "ws";
    const token = getAuthToken();
    const topic = `calls:${(user as any).organizationId}`;
    const url = `${proto}://${window.location.host}/ws/live-chat?topic=${encodeURIComponent(topic)}${token ? `&access_token=${encodeURIComponent(token)}` : ""}`;
    const ws = new WebSocket(url);
    ws.onmessage = () => {
      queryClient.invalidateQueries({ queryKey: ["/api/calls"] });
      queryClient.invalidateQueries({ queryKey: ["/api/live-agents"] });
    };
    return () => { try { ws.close(); } catch {} };
  }, [user?.id]);

  const live = (calls.data || []).filter(c => ["ringing", "active", "on_hold"].includes(c.status));

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-4" data-testid="page-supervisor-dashboard">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Supervisor Dashboard</h1>
        <p className="text-sm text-muted-foreground">Live calls, agents, and queue depth.</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <KPI icon={<PhoneCall className="w-4 h-4" />} label="Total" value={summary.data?.totalCalls ?? 0} />
        <KPI icon={<Headphones className="w-4 h-4" />} label="Answered" value={summary.data?.answered ?? 0} />
        <KPI icon={<Clock className="w-4 h-4" />} label="Avg wait" value={`${summary.data?.avgWaitSec ?? 0}s`} />
        <KPI icon={<Users className="w-4 h-4" />} label="Agents" value={(agents.data || []).length} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card data-testid="card-live-calls">
          <CardHeader><CardTitle className="text-sm">Live Calls ({live.length})</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {live.length === 0 && <p className="text-xs text-muted-foreground">No active calls.</p>}
            {live.map(c => (
              <div key={c.id} className="flex items-center justify-between border rounded-md p-2" data-testid={`row-live-call-${c.id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{c.callerName || c.callerPhone}</div>
                  <div className="text-xs text-muted-foreground truncate">{c.queueName} → {c.agentName || "(unassigned)"}</div>
                </div>
                <Badge variant="outline">{c.status}</Badge>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card data-testid="card-agents">
          <CardHeader><CardTitle className="text-sm">Agents</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {(agents.data || []).map(a => (
              <div key={a.id} className="flex items-center justify-between border rounded-md p-2" data-testid={`row-agent-${a.id}`}>
                <div className="text-sm">{a.name || a.id.slice(0, 8)}</div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">{a.activeCalls ?? 0} calls</span>
                  <Badge variant={a.status === "available" ? "default" : "secondary"}>{a.status}</Badge>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card data-testid="card-queues" className="lg:col-span-2">
          <CardHeader><CardTitle className="text-sm">Queues</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {(queues.data || []).map(q => (
              <div key={q.id} className="border rounded-md p-3" data-testid={`row-queue-${q.id}`}>
                <div className="text-sm font-medium">{q.name}</div>
                <div className="text-xs text-muted-foreground mt-1">Waiting: {q.waiting ?? 0} · Avail: {q.agentsAvailable ?? 0}</div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function KPI({ icon, label, value }: { icon: React.ReactNode; label: string; value: number | string }) {
  return (
    <Card data-testid={`kpi-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <CardContent className="p-3 flex items-center gap-3">
        <div className="text-muted-foreground">{icon}</div>
        <div>
          <div className="text-xs text-muted-foreground">{label}</div>
          <div className="text-lg font-semibold">{value}</div>
        </div>
      </CardContent>
    </Card>
  );
}
